import * as React from 'react';
import {
  Button,
  Select,
  makeStyles,
  tokens,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { Project, ProjectCreate } from '../../../types/project';
import { ApiErrorInfo } from '../../../types/api';
import { useIntegration } from '../../../hooks/useIntegration';
import { useEvent } from '../../../hooks/useEvent';
import { useMarker } from '../../../hooks/useMarker';
import ProjectEventsGrid from './ProjectEventsGrid';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
  },
});

interface ProjectFormProps {
  project?: Project;
  onSave: (project: ProjectCreate) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: ApiErrorInfo | null;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  onSave,
  onCancel,
  loading,
  error
}) => {
  const styles = useStyles();
  const { integrations, listLoading: integrationsLoading, fetchIntegrations } = useIntegration();
  const { events, fetchEvents } = useEvent();
  const { markers, fetchMarkers } = useMarker();

  const [formData, setFormData] = React.useState<ProjectCreate>(
    project ? {
      descricao: project.descricao || '',
      tipo: project.tipo || 1,
      integracaoId: project.integracaoId || 0,
      eventosProjetos: project.eventosProjetos?.map(evento => {
        // Verificar se os dados estão aninhados em evento.evento ou diretamente
        const sourceEvento = (evento as any).evento || evento;
        return {
          eventoId: evento.eventoId,
          descricao: sourceEvento.descricao,
          detalhe: sourceEvento.detalhe,
          severidade: sourceEvento.severidade,
          pontuacao: evento.pontuacao || sourceEvento.pontuacao || 0,
          marcadorId: evento.marcadorId,
          consultaERPId: evento.consultaERPId || sourceEvento.consultaERPId || 0,
        };
      }) || [],
    } : {
      descricao: '',
      tipo: 1,
      integracaoId: 0,
      eventosProjetos: [],
    }
  );

  const [fieldErrors, setFieldErrors] = React.useState({
    descricao: '',
    tipo: '',
    integracaoId: '',
  });

  // Carregar integrações quando o componente montar
  React.useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Carregar eventos quando o componente montar
  React.useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Carregar marcadores quando o componente montar
  React.useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  // Atualizar formData quando o project prop mudar (útil para edição assíncrona)
  React.useEffect(() => {
    if (project) {
      setFormData(prev => ({
        ...prev,
        descricao: project.descricao || '',
        tipo: project.tipo || 0,
        integracaoId: project.integracaoId || 0,
        eventosProjetos: project.eventosProjetos?.map(evento => {
          // Verificar se os dados estão aninhados em evento.evento ou diretamente
          const sourceEvento = (evento as any).evento || evento;
          return {
            eventoId: evento.eventoId,
            descricao: sourceEvento.descricao,
            detalhe: sourceEvento.detalhe,
            severidade: sourceEvento.severidade,
            pontuacao: evento.pontuacao || sourceEvento.pontuacao || 0,
            marcadorId: evento.marcadorId,
            consultaERPId: evento.consultaERPId || sourceEvento.consultaERPId || 0,
          };
        }) || [],
      }));
    }
  }, [project]);

  const validateField = (field: keyof typeof fieldErrors, value: string | number, touched: boolean = false) => {
    let error = '';

    switch (field) {
      case 'descricao':
        if (!value.toString().trim()) {
          error = 'Descrição é obrigatória';
        }
        break;
      case 'tipo':
        if (value === null || value === undefined) {
          error = 'Tipo é obrigatório';
        }
        break;
      case 'integracaoId':
        if (value === null || value === undefined || value === 0) {
          error = 'Integração é obrigatória';
        }
        break;
    }

    setFieldErrors(prev => ({ ...prev, [field]: touched ? error : '' }));
    return error === '';
  };

  const handleInputChange = (field: keyof ProjectCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validar campo em tempo real apenas se já foi tocado
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      validateField(field as keyof typeof fieldErrors, value, true);
    }
  };

  const handleBlur = (field: keyof typeof fieldErrors) => {
    validateField(field, formData[field as keyof typeof formData] as string | number, true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Marcar todos os campos como tocados e validar
    setFieldErrors({
      descricao: formData.descricao.trim() ? '' : 'Descrição é obrigatória',
      tipo: formData.tipo !== null && formData.tipo !== undefined ? '' : 'Tipo é obrigatório',
      integracaoId: formData.integracaoId && formData.integracaoId > 0 ? '' : 'Integração é obrigatória',
    });

    // Verificar se há erros
    const hasErrors = !formData.descricao.trim() || formData.tipo === null || formData.tipo === undefined || !formData.integracaoId || formData.integracaoId <= 0;

    if (hasErrors) {
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {loading && (
        <MessageBar intent="info">
          <MessageBarBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              Estamos salvando o projeto. Isso pode levar alguns segundos...
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent={error.isAuthError ? "warning" : "error"}>
          <MessageBarBody>
            {error.isNotFound && "Projeto não encontrado. Verifique se o ID está correto."}
            {error.isValidationError && error.message}
            {error.isAuthError && "Acesso negado. Você não tem permissão para esta operação."}
            {!error.isNotFound && !error.isValidationError && !error.isAuthError && error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.section}>
        <Field
          label="Descrição"
          required
          validationMessage={fieldErrors.descricao}
          validationState={fieldErrors.descricao ? 'error' : 'none'}
        >
          <Textarea
            size="large"
            value={formData.descricao}
            onChange={(e) => handleInputChange('descricao', e.target.value)}
            onBlur={() => handleBlur('descricao')}
            placeholder="Descrição detalhada do projeto"
            rows={4}
            resize="vertical"
          />
        </Field>

        <div className={styles.row}>
          <Field
            label="Tipo"
            required
            validationMessage={fieldErrors.tipo}
            validationState={fieldErrors.tipo ? 'error' : 'none'}
          >
            <Select
              size="large"
              value={formData.tipo.toString()}
              onChange={(e, data) => handleInputChange('tipo', parseInt(data.value || '1'))}
            >
              <option value="1">Implantação</option>
              <option value="2">Revitalização</option>
              <option value="3">Fast Track</option>
              <option value="4">Oraculo</option>
            </Select>
          </Field>

          <Field
            label="Integração"
            required
            validationMessage={fieldErrors.integracaoId}
            validationState={fieldErrors.integracaoId ? 'error' : 'none'}
          >
            <Select
              size="large"
              value={formData.integracaoId.toString()}
              onChange={(e, data) => handleInputChange('integracaoId', parseInt(data.value || '0'))}
              disabled={integrationsLoading}
            >
              <option value="0">Selecione uma integração...</option>
              {integrations.map((integration) => (
                <option key={integration.id} value={integration.id.toString()}>
                  {integration.empresa}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Seção de Eventos do Projeto */}
        <ProjectEventsGrid
          eventosProjetos={formData.eventosProjetos || []}
          events={events}
          markers={markers}
          onChange={(eventos) => setFormData(prev => ({ ...prev, eventosProjetos: eventos }))}
          loading={loading}
        />
      </div>

      <div className={styles.actions}>
        <Button type="button" appearance="subtle" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          appearance="primary"
          disabled={loading || Object.values(fieldErrors).some(error => error !== '')}
        >
          {loading ? 'Salvando...' : project ? 'Atualizar' : 'Criar'} Projeto
        </Button>
      </div>
    </form>
  );
};

export default ProjectForm;